import React, { FC, useRef, useState } from "react";
import Avatar from "components/Avatar/Avatar";
import NcDropDown from "components/NcDropDown/NcDropDown";
import CommentCardLikeReplyContainer from "containers/CommentCardLikeReplyContainer/CommentCardLikeReplyContainer";
import { PostAuthorType } from "data/types";
import { Link } from "react-router-dom";
import twFocusClass from "utils/twFocusClass";
import SingleCommentForm from "containers/PageSingle/SingleCommentForm";
import ModalEditComment from "./ModalEditComment";
import ModalDeleteComment from "./ModalDeleteComment";
import ModalReportItem from "components/ModalReportItem/ModalReportItem";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  increment,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  setDoc
} from "firebase/firestore";
import {auth, db} from "../../configs/firebase-config";
import {displayToast, toastType} from "../../global/Functions";

export interface CommentType {
  id: number;
  author: PostAuthorType;
  date: string;
  content: string;
  parentId: number | null;
  children?: CommentType[];
  like: {
    count: number;
    isLiked: boolean;
  };
}

export interface CommentCardProps {
  className?: string;
  comment: any;
  size?: "large" | "normal";
  data?: any
}

const CommentCard: FC<CommentCardProps> = ({
  className = "",
  comment,
  size = "large",
  data
}) => {
  const { author, id, date, parentId, content } = comment;
  const actions = [
    { id: "edit", name: "Edit", icon: "las la-edit" },
    { id: "reply", name: "Reply", icon: "las la-reply" },
    { id: "report", name: "Report abuse", icon: "las la-flag" },
    { id: "delete", name: "Delete", icon: "las la-trash-alt" },
  ];

  const textareaRef = useRef(null);
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [nestedComment, setNestedComment] = useState("");

  const openReplyForm = () => {
    if (auth.currentUser?.uid) {
      setIsReplying(true);
      setTimeout(() => {
        textareaRef.current && (textareaRef.current as any).focus();
      }, 100);
    } else displayToast("Log in to leave a comment", toastType.info);
  };
  const closeReplyForm = () => {
    setIsReplying(false);
  };

  const openModalEditComment = () => setIsEditing(true);
  const closeModalEditComment = () => setIsEditing(false);

  const openModalReportComment = () => setIsReporting(true);
  const closeModalReportComment = () => setIsReporting(false);

  const openModalDeleteComment = () => setIsDeleting(true);
  const closeModalDeleteComment = () => setIsDeleting(false);

  const handleClickDropDown = (item: typeof actions[number]) => {
    if (item.id === "reply") {
      return openReplyForm();
    }
    if (item.id === "edit") {
       if (auth.currentUser?.uid === author.id){
         return openModalEditComment();
       } else displayToast("You do not have permission to edit", toastType.warn);
    }
    if (item.id === "report") {
      return openModalReportComment();
    }
    if (item.id === "delete") {
      if (auth.currentUser?.uid === author.id){
        return openModalDeleteComment();
      } else displayToast("You do not have permission to delete", toastType.warn);
    }
    return;
  };

  const renderCommentForm = () => {
    const submitComment = async (e: any, commentId: any) => {
      e.preventDefault();
      const postDocRef = doc(db, "posts", `${data.userId}/posts/${data.id}`);
      const commentCollectionRef = collection(postDocRef, "comments");
      const userData = await getDoc(doc(db, "users", `${auth.currentUser?.uid}`))
      const docID = `${data.commentCount + 1}_${Date.now()}`;

      try {
        await setDoc(doc(commentCollectionRef, docID), {
          id: docID,
          author: {
            id: auth.currentUser?.uid,
            href: `/author/the-demo-author-slug/${auth.currentUser?.uid}`,
            count: 0,
            desc: "Web 3 enthusiast",
            jobName: "Author Job",
            avatar: auth.currentUser?.photoURL,
            ...userData.data()
          },
          createdAt: serverTimestamp(),
          content: nestedComment,
          parentId: commentId,
          heartCount: 0,
          childrens: null,
          postId: data.id
        })
        await updateDoc(postDocRef, {commentCount: increment(1), priority: increment(5)})
        await updateDoc(doc(commentCollectionRef, `${commentId}`), {childrens: arrayUnion(docID)});
      } catch (error) {
        displayToast(error.message, toastType.error);
      }
      closeReplyForm();
      e.target.reset();
    }
    return (
      <SingleCommentForm
        textareaRef={textareaRef}
        onChange={(e) => setNestedComment(e.target.value)}
        onClickSubmit={async (e) => await submitComment(e, comment.id)}
        onClickCancel={() => { closeReplyForm(); setNestedComment("")}}
        className="flex-grow"
        commentId={id}
      />
    );
  };

  return (
    <>
      {author
      ?
        <div
          className={`nc-CommentCard flex ${className}`}
          data-nc-id="CommentCard"
          data-comment-id={id}
          data-comment-parent-id={parentId}
        >
          {author.avatar ?
            <Avatar
              imgUrl={author.avatar}
              userName={author.displayName}
              sizeClass={`h-6 w-6 text-base ${
                size === "large" ? "sm:text-lg sm:h-8 sm:w-8" : ""
              }`}
              radius="rounded-full"
              containerClassName="mt-4"
            />
            :
            <></>
          }
          <div className="flex-grow flex flex-col p-4 ml-2 text-sm border border-neutral-200 rounded-xl sm:ml-3 sm:text-base dark:border-neutral-700">
            {/* AUTHOR INFOR */}
            <div className="relative flex items-center pr-6">
              <div className="absolute -right-3 -top-3">
                <NcDropDown
                  className={`p-2 text-neutral-500 flex items-center justify-center rounded-lg hover:text-neutral-800 dark:hover:text-neutral-200 sm:hover:bg-neutral-100 dark:hover:bg-neutral-800 ${twFocusClass()}`}
                  data={actions}
                  onClick={handleClickDropDown}
                />
              </div>
              <Link
                className="flex-shrink-0 font-semibold text-neutral-800 dark:text-neutral-100"
                to={author.href}
              >
                {author.displayName}
              </Link>
              <span className="mx-2">·</span>
              <span className="text-neutral-500 dark:text-neutral-400 text-xs line-clamp-1 sm:text-sm">
              {date}
            </span>
            </div>

            {/* CONTENT */}
            <span className="block text-neutral-700 mt-2 mb-3 sm:mt-3 sm:mb-4 dark:text-neutral-300">
            {content}
          </span>

            {/* ACTION LIKE REPLY */}
            {isReplying ? (
              renderCommentForm()
            ) : (
              <CommentCardLikeReplyContainer
                onClickReply={openReplyForm}
                comment={comment}
                userId={data.userId}
                postId={data.id}
              />
            )}
          </div>
        </div>
        :
        <></>
      }

      <ModalEditComment
        show={isEditing}
        comment={comment}
        data={data}
        onCloseModalEditComment={closeModalEditComment}
      />
      <ModalReportItem
        show={isReporting}
        id={comment.id}
        postId={comment.postId}
        onCloseModalReportItem={closeModalReportComment}
      />
      <ModalDeleteComment
        show={isDeleting}
        comment={comment}
        data={data}
        onCloseModalDeleteComment={closeModalDeleteComment}
      />
    </>
  );
};

export default CommentCard;
